/**
 * CreateChargeModal - Modal premium para criação e edição de cobranças financeiras
 * 
 * Seções: Cliente, Itens (catálogo), Tipo, Vencimento, Pagamento, Conta, Observações
 * Integra ProductCatalogModal para seleção de produtos/serviços do catálogo
 * Suporta modo edição via prop editCobrancaId (popula dados e faz update)
 * 
 * @author Módulo Financeiro
 * @version 2.0.0
 * RELEASE: 2026-03-15 v1.0.0 - Criação do modal completo
 * RELEASE: 2026-03-15 v1.1.0 - Layout tabela + ProductCatalogModal + totalizadores
 * RELEASE: 2026-03-15 v2.0.0 - Modo edição unificado + validação CPF/CNPJ
 * RELEASE: 2026-06-18 v2.1.1 - Bloqueia troca de meio de pagamento em
 *                              parcelamento já sincronizado com provider,
 *                              pois a API pública do Asaas não expõe edição
 *                              de billingType no parcelamento-pai.
 * RELEASE: 2026-06-18 v2.1.2 - Bloqueia edição de valor/itens em parcelamento
 *                              Asaas com forma "Pergunte ao Cliente" já emitido,
 *                              evitando erro invalid_value e divergência provider.
 * RELEASE: 2026-09-12 v2.2.0 - Inverte ordem: Conta Corrente antes do Meio de
 *                              Pagamento; meios filtrados pela conta (globais
 *                              sem vínculo aparecem em todas); troca de conta
 *                              desmarca meio incompatível.
 */

import React, { useState, useMemo, useCallback, useEffect, useRef } from 'react';
import { formatDocumentForDisplay } from '@/lib/input-masks';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { CurrencyInput } from '@/components/ui/currency-input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Badge } from '@/components/ui/badge';
import { Separator } from '@/components/ui/separator';
import { ScrollArea } from '@/components/ui/scroll-area';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '@/components/ui/popover';
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from '@/components/ui/command';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { Alert, AlertDescription } from '@/components/ui/alert';
import {
  Receipt,
  Plus,
  Trash2,
  Loader2,
  CalendarDays,
  CreditCard,
  Building2,
  Package,
  Wrench,
  User,
  Check,
  ChevronsUpDown,
  FileText,
  AlertTriangle,
  ShoppingCart,
  Percent,
  Calculator,
  CalendarClock,
  Bell,
} from 'lucide-react';
import { Switch } from '@/components/ui/switch';
import { InlineNotice } from '@/components/ui/inline-notice';
// Trava de valor mínimo por parcela conforme provider gateway (espelhada no SQL).
import { validateInstallmentMinimum } from '@/lib/financial/providerMinimums';
import { cn } from '@/lib/utils';
import { format, addDays } from 'date-fns';
import { ptBR } from 'date-fns/locale';

import { ProductCatalogModal } from '@/components/crm/deals/modal/tabs/products/ProductCatalogModal';
import { CustomerFormDialog } from '@/components/crm/customers/CustomerFormDialog';
import { DealFormDialog } from '@/components/crm/deals/DealFormDialog';
import { InlineEditableField } from '@/components/crm/deals/modal/tabs/products/InlineEditableField';
import { formatCurrency } from '@/lib/currency-utils';
import { formatDealCode } from '@/lib/formatters';
import { DealStatusBadgePremium } from '@/components/crm/deals/modal/components/DealStatusBadgePremium';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { useCrmProducts } from '@/hooks/useCrmProducts';
import { useCrmCustomersPaginated, PaginatedCustomer } from '@/hooks/useCrmCustomersPaginated';
import { useFinanceiroMeiosPagamento, FinanceiroMeioPagamento } from '@/hooks/useFinanceiroMeiosPagamento';
import { useFinanceiroContaCorrente, FinanceiroContaCorrente } from '@/hooks/useFinanceiroContaCorrente';
import { useCreateCharge, ChargeItemInput } from '@/hooks/useCreateCharge';
import { useCobrancaDetails } from '@/hooks/useCobrancaDetails';
import { createLogger } from '@/lib/logger';
import { toast } from 'sonner';
// v2.1.0 (2026-06-17): juros/multa/expiração também em cobrança avulsa e parcelada
import {
  SubscriptionJurosSection,
  JUROS_DEFAULT,
  type JurosValue,
} from '@/components/financial/subscription/SubscriptionJurosSection';
import { SubscriptionExpiracaoSection } from '@/components/financial/subscription/SubscriptionExpiracaoSection';
import { VehiclePickerSection } from '@/components/financial/VehiclePickerSection';
import { DescricaoTemplateField } from '@/components/financial/DescricaoTemplateField';
import { persistDescricaoTemplate, hasDescricaoVariables } from '@/hooks/financial/useFinanceiroDescricaoTemplate';
// v2.2.0 (2026-06-20): prefill de juros/multa e expiração via padrões do tenant
import { useChargeDefaults } from '@/hooks/useChargeDefaults';



const logger = createLogger('CreateChargeModal');

/** Props do componente */
interface CreateChargeModalProps {
  /** Controla abertura do modal */
  open: boolean;
  /** Callback para fechar */
  onOpenChange: (open: boolean) => void;
  /** ID do deal para pré-preencher (opcional) */
  prefillDealId?: string | null;
  /** ID do cliente para pré-preencher (opcional) */
  prefillCustomerId?: string | null;
  /** ID da tarefa de origem (aba Financeiro do modal de tarefa) */
  prefillTaskId?: string | null;
  /** Se true, trava seletores de deal e cliente (cobrança criada a partir de um negócio) */
  lockDeal?: boolean;
  /** ID da cobrança para modo edição (se fornecido, modal entra em modo edição) */
  editCobrancaId?: string | null;
}

/** Valor mínimo exigido pelo provider Asaas (em BRL) */
const VALOR_MINIMO_COBRANCA = 5;

/**
 * Descrição da linha automática criada pelo backend quando as parcelas de uma
 * cobrança manual (conta sem integração) somam valor diferente dos produtos.
 * Ela é somente leitura na interface e nunca é enviada como produto.
 */
const AJUSTE_PARCELAS_DESCRICAO = 'Ajuste de parcelas';

/** Modo de desconto: percentual ou valor fixo */
type DiscountMode = 'percent' | 'fixed';

/** Item interno do formulário (com product_id e dados do catálogo) */
interface FormItem {
  id: string; // ID temporário para key do React
  product_id: string;
  product_name: string;
  product_sku: string | null;
  descricao: string;
  quantidade: number;
  valor_unitario: number;
  desconto_percent: number; // Desconto em percentual (0-100)
  desconto_fixed: number;   // Desconto em valor fixo (R$)
}

/**
 * Toggle compacto para alternar entre modo % e R$
 * Replica o mesmo padrão visual do DealItemsSummary
 */
function DiscountModeToggle({
  mode,
  onToggle,
}: {
  mode: DiscountMode;
  onToggle: (newMode: DiscountMode) => void;
}) {
  return (
    <div className="inline-flex rounded-md border border-border/60 overflow-hidden h-6">
      <button
        type="button"
        onClick={() => onToggle('percent')}
        className={cn(
          'px-1.5 text-[10px] font-medium transition-colors',
          mode === 'percent'
            ? 'bg-primary text-primary-foreground'
            : 'bg-muted/30 text-muted-foreground hover:bg-muted/60'
        )}
        title="Desconto em percentual"
      >
        %
      </button>
      <button
        type="button"
        onClick={() => onToggle('fixed')}
        className={cn(
          'px-1.5 text-[10px] font-medium transition-colors',
          mode === 'fixed'
            ? 'bg-primary text-primary-foreground'
            : 'bg-muted/30 text-muted-foreground hover:bg-muted/60'
        )}
        title="Desconto em valor fixo"
      >
        R$
      </button>
    </div>
  );
}

// Mapa de ícones SVG profissionais para meios de pagamento
import { PaymentMethodIcon, DefaultPaymentIcon } from '@/components/icons/PaymentMethodIcons';

/**
 * Modal premium de criação de cobrança
 */
export function CreateChargeModal({ open, onOpenChange, prefillDealId, prefillCustomerId, prefillTaskId, lockDeal, editCobrancaId }: CreateChargeModalProps) {
  const queryClient = useQueryClient();

  /** Indica se o modal está em modo edição */
  const isEditMode = !!editCobrancaId;

  // === RPC de detalhes para modo edição ===
  const { data: editDetails, isLoading: loadingEditDetails } = useCobrancaDetails(editCobrancaId, open && isEditMode);

  /**
   * Cobrança vinculada a uma recorrência (assinatura).
   * Quando true, o "Tipo da Cobrança" não pode ser alterado para evitar quebra
   * de sincronização com o provider (ex.: parcelas geradas pela assinatura no Asaas).
   */
  const isRecurrenceCharge =
    isEditMode &&
    (!!editDetails?.cobranca?.assinatura_id ||
      editDetails?.cobranca?.tipo_cobranca === 'recorrente');

  /**
   * Cobrança já sincronizada com o provider (Asaas/etc.).
   * Quando true, o número de parcelas e o tipo de cobrança não podem ser
   * alterados — a RPC `update_financeiro_cobranca` v3.0.0 também bloqueia.
   */
  const isSyncedCharge = isEditMode && !!editDetails?.cobranca?.provider_charge_id;

  /**
   * Parcelamento já emitido no provider não pode trocar meio de pagamento via
   * API pública do Asaas. O painel web usa API interna; pela API pública só é
   * seguro editar valor/vencimento/descrição das parcelas.
   */
  const lockPaymentMethodForSyncedInstallment =
    isSyncedCharge && editDetails?.cobranca?.tipo_cobranca === 'parcelada';

  // === Estados do formulário ===
  const [customerId, setCustomerId] = useState<string | null>(null);
  /** ID do negócio vinculado à cobrança (opcional) */
  const [dealId, setDealId] = useState<string | null>(null);
  /** Veículos do cliente vinculados à cobrança (módulo Veículos) */
  const [vehicleIds, setVehicleIds] = useState<string[]>([]);

  // Limpa a seleção de veículos sempre que o cliente muda (os veículos
  // pertencem ao cliente selecionado).
  useEffect(() => {
    setVehicleIds([]);
  }, [customerId]);

  // Cliente selecionado salvo localmente (para exibir nome mesmo após nova busca)
  const [selectedCustomerObj, setSelectedCustomerObj] = useState<PaginatedCustomer | null>(null);
  const [tipoCobranca, setTipoCobranca] = useState<'unica' | 'parcelada'>('unica');
  const [numeroParcelas, setNumeroParcelas] = useState(2);
  const [dataVencimento, setDataVencimento] = useState(
    format(addDays(new Date(), 3), 'yyyy-MM-dd')
  );
  const [meioPagamentoId, setMeioPagamentoId] = useState<string | null>(null);
  const [contaCorrenteId, setContaCorrenteId] = useState<string | null>(null);
  const [observacoes, setObservacoes] = useState('');
  // Descrição livre da cobrança (editável pelo usuário; antes era auto-gerada a partir dos itens)
  const [descricao, setDescricao] = useState('');
  const [itens, setItens] = useState<FormItem[]>([]);
  /**
   * v2.8.0 — valor da linha automática "Ajuste de parcelas" (somente leitura).
   * Positivo quando as parcelas somam mais que os produtos; negativo quando
   * somam menos. Só existe em cobranças de conta sem integração.
   */
  const [ajusteParcelas, setAjusteParcelas] = useState(0);
  // Modo de desconto por item (% ou R$)
  const [discountModes, setDiscountModes] = useState<Record<string, DiscountMode>>({});
  // Controle do modal de catálogo de produtos
  const [catalogModalOpen, setCatalogModalOpen] = useState(false);
  // Controle do modal de criação de cliente
  const [createCustomerOpen, setCreateCustomerOpen] = useState(false);
  /** Controle do modal de criação de negócio inline */
  const [createDealOpen, setCreateDealOpen] = useState(false);
  // Controle do popover de seleção de negócio
  const [dealPopoverOpen, setDealPopoverOpen] = useState(false);
  /** Flag para evitar re-popular os dados quando editDetails já foi carregado */
  const [editPopulated, setEditPopulated] = useState(false);
  /** Estado de salvamento para modo edição */
  const [isSaving, setIsSaving] = useState(false);
  /**
   * v2.1.0 — Juros/multa e prazo de expiração da cobrança (paridade c/ assinatura).
   * Persistidos via RPC create/update_financeiro_cobranca em colunas dedicadas.
   */
  const [juros, setJuros] = useState<JurosValue>(JUROS_DEFAULT);
  const [diasExpiracao, setDiasExpiracao] = useState<number>(60);
  /**
   * v2.7.0 — Lembretes automáticos (régua de cobrança) desta cobrança.
   * Persistido junto com os demais campos (p_reminders_enabled) — o modal de
   * detalhes apenas exibe o estado, sem permitir alteração.
   */
  const [remindersEnabled, setRemindersEnabled] = useState<boolean>(true);


  // === Estados do seletor de cliente com busca server-side ===
  const [customerPopoverOpen, setCustomerPopoverOpen] = useState(false);
  const [customerSearch, setCustomerSearch] = useState('');
  const [debouncedCustomerSearch, setDebouncedCustomerSearch] = useState('');

  // Debounce de 300ms na busca do cliente
  useEffect(() => {
    const timer = setTimeout(() => setDebouncedCustomerSearch(customerSearch), 300);
    return () => clearTimeout(timer);
  }, [customerSearch]);

  // === Hooks de dados ===
  const { customers, isLoading: loadingCustomers } = useCrmCustomersPaginated(
    { search: debouncedCustomerSearch || null },
    { page: 1, pageSize: 20 }
  );
  const { products } = useCrmProducts();
  const { meios, isLoading: loadingMeios } = useFinanceiroMeiosPagamento();
  const { contas, isLoading: loadingContas } = useFinanceiroContaCorrente();
  const { createCharge, isCreating } = useCreateCharge();
  // v2.2.0 — Padrões do tenant para prefill no modo criação (apenas UI).
  const chargeDefaults = useChargeDefaults();


  // === Query para buscar cliente quando vem do prefill (deal → cobrança) ===
  // Necessário porque o prefill só passa o ID, mas o seletor precisa do objeto completo
  const { data: prefillCustomerData } = useQuery({
    queryKey: ['prefill-customer', prefillCustomerId],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('crm_customers')
        .select('id, nome, razao_social, nome_fantasia, customer_type, cpf, cnpj, main_contact_id')
        .eq('id', prefillCustomerId!)
        .single();
      if (error) throw error;
      return data;
    },
    enabled: !!prefillCustomerId && open,
  });

  // === Query de negócios do cliente selecionado ===
  // Busca deals vinculados via contact_id (main_contact_id do cliente) OU customer_id
  const mainContactId = selectedCustomerObj?.main_contact_id;
  const { data: customerDeals, isLoading: loadingDeals } = useQuery({
    queryKey: ['customer-deals-for-charge', customerId, mainContactId],
    queryFn: async () => {
      if (!customerId) return [];
      // Monta filtro OR: contact_id do contato principal OU customer_id direto
      const orFilters = [
        `customer_id.eq.${customerId}`,
        ...(mainContactId ? [`contact_id.eq.${mainContactId}`] : []),
      ].join(',');
      const { data, error } = await supabase
        .from('crm_deals')
        .select('id, title, value, currency, status')
        .or(orFilters)
        .order('created_at', { ascending: false });
      if (error) throw error;
      return data || [];
    },
    enabled: !!customerId,
    staleTime: 30_000,
  });

  // === Query de itens do negócio selecionado ===
  // Busca produtos/serviços vinculados ao deal para auto-popular a seção de itens
  const { data: dealItems, dataUpdatedAt: dealItemsUpdatedAt } = useQuery({
    queryKey: ['deal-items-for-charge', dealId],
    queryFn: async () => {
      if (!dealId) return [];
      const { data, error } = await supabase
        .from('crm_deal_items')
        .select(`
          *,
          product:crm_products(id, name, sku, product_type, default_price)
        `)
        .eq('deal_id', dealId)
        .order('sort_order', { ascending: true });
      if (error) {
        logger.error('Erro ao buscar itens do deal para cobrança:', error.message);
        throw error;
      }
      return data || [];
    },
    enabled: !!dealId,
    staleTime: 0, // Sempre buscar dados frescos para evitar cache desatualizado
  });

  // Ref para rastrear timestamp da última auto-população de itens do deal
  // Resolve race condition: permite re-population quando dados frescos chegam do refetch
  const lastPopulatedAtRef = useRef<number>(0);

  // === Invalidar cache de itens do deal ao abrir o modal ===
  // Garante que dados sempre venham frescos do banco, evitando valores desatualizados
  useEffect(() => {
    if (open && dealId && !isEditMode) {
      // Reseta estado local e ref para permitir re-population com dados frescos
      setItens([]);
      setDiscountModes({});
      lastPopulatedAtRef.current = 0;
      queryClient.invalidateQueries({ queryKey: ['deal-items-for-charge', dealId] });
    }
  }, [open, dealId, queryClient, isEditMode]);

  // === Auto-popular itens quando deal é selecionado ===
  // Converte DealItem[] para FormItem[] preservando quantidade, preço e descontos
  // Usa dataUpdatedAt para detectar dados frescos e re-popular mesmo com itens já presentes
  useEffect(() => {
    // Guard: só executa com modal aberto e itens disponíveis
    if (!open) return;
    if (!dealItems || dealItems.length === 0) return;
    // Não sobrescrever itens já populados em modo edição
    if (isEditMode) return;
    // Só popula se os dados são mais recentes que a última população (resolve race condition)
    if (dealItemsUpdatedAt <= lastPopulatedAtRef.current) return;

    const mappedItems: FormItem[] = dealItems.map((item: any) => ({
      id: crypto.randomUUID(),
      product_id: item.product_id,
      product_name: item.product?.name || 'Produto',
      product_sku: item.product?.sku || null,
      descricao: item.product?.name || 'Produto',
      quantidade: item.quantity,
      valor_unitario: item.unit_price,
      desconto_percent: item.discount_percent || 0,
      desconto_fixed: item.discount_fixed || 0,
    }));

    // Popular modos de desconto para itens com desconto fixo
    const newDiscountModes: Record<string, DiscountMode> = {};
    mappedItems.forEach(item => {
      if (item.desconto_fixed > 0 && item.desconto_percent === 0) {
        newDiscountModes[item.id] = 'fixed';
      }
    });

    // Registra timestamp para evitar re-população com mesmos dados
    lastPopulatedAtRef.current = dealItemsUpdatedAt;
    setItens(mappedItems);
    setDiscountModes(prev => ({ ...prev, ...newDiscountModes }));
    toast.success(`${mappedItems.length} ${mappedItems.length === 1 ? 'item carregado' : 'itens carregados'} do negócio`);
    logger.info('Itens do deal carregados automaticamente', { dealId, count: mappedItems.length });
  }, [dealItems, dealItemsUpdatedAt, open]);

  // Filtrar apenas meios ativos
  const meiosAtivos = useMemo(() => meios.filter(m => m.is_active), [meios]);

  /** Meio de pagamento atualmente selecionado no formulário. */
  const meioPagamentoSelecionado = useMemo(
    () => meios.find(m => m.id === meioPagamentoId) || null,
    [meios, meioPagamentoId]
  );

  /**
   * v1.4.0 (2026-06-20): regra de produto — cobrança parcelada já emitida no
   * provider NÃO permite edição de valor (itens, qtd, preço, desconto). Para
   * alterar o valor, o usuário deve cancelar e recriar a cobrança. Substitui
   * o lock anterior que só travava em billingType='indefinido'.
   */
  const lockValueForSyncedInstallment = lockPaymentMethodForSyncedInstallment;

  /**
   * Contas disponíveis para cobrança.
   *
   * v2026-09-12: passam a incluir contas SEM integração de pagamento
   * (`provider_slug` nulo). Nesse caso a cobrança é apenas de controle
   * interno (modo manual) — nada é enviado ao provedor e a baixa é feita
   * manualmente pelo usuário.
   */
  const contasDisponiveis = useMemo(
    () => contas.filter(c => c.is_active),
    [contas]
  );

  // === Limite de caracteres para observações (Asaas = 500 chars) ===
  const MAX_OBS_ASAAS = 500;
  const contaSelecionada = contasDisponiveis.find(c => c.id === contaCorrenteId);
  const isProviderAsaas = contaSelecionada?.provider_slug === 'asaas';
  /** Conta sem integração => cobrança manual (sem envio ao provedor) */
  const isContaManual = !!contaSelecionada && !contaSelecionada.provider_slug;

  // Auto-selecionar conta se houver apenas uma
  React.useEffect(() => {
    if (contasDisponiveis.length === 1 && !contaCorrenteId) {
      setContaCorrenteId(contasDisponiveis[0].id);
    }
  }, [contasDisponiveis, contaCorrenteId]);

  /**
   * v2026-09-12 (filtro por conta): meios de pagamento exibidos são os
   * vinculados à conta selecionada + os GLOBAIS (sem conta vinculada).
   * Evita misturar meios de contas diferentes na mesma tela.
   */
  const meiosDaConta = useMemo(
    () =>
      meiosAtivos.filter(
        m => !m.conta_corrente_id || m.conta_corrente_id === contaCorrenteId
      ),
    [meiosAtivos, contaCorrenteId]
  );

  // Ao trocar a conta, desmarca o meio selecionado se ele não pertencer
  // à nova conta (e não for global), evitando envio de combinação inválida.
  React.useEffect(() => {
    if (!meioPagamentoId) return;
    const meioAtual = meios.find(m => m.id === meioPagamentoId);
    if (
      meioAtual?.conta_corrente_id &&
      meioAtual.conta_corrente_id !== contaCorrenteId
    ) {
      setMeioPagamentoId(null);
    }
  }, [contaCorrenteId, meioPagamentoId, meios]);

  // Nome de exibição do cliente paginado
  const getCustomerDisplayNamePaginated = (customer: PaginatedCustomer) => {
    if (customer.customer_type === 'pj') {
      return customer.nome_fantasia || customer.razao_social || 'Empresa sem nome';
    }
    return customer.nome || 'Cliente sem nome';
  };

  // Documento do cliente para exibição secundária
  const getCustomerDocument = (customer: PaginatedCustomer) => {
    return customer.cnpj || customer.cpf || null;
  };

  /** Calcula o desconto absoluto de um item com base no modo ativo */
  const calcItemDiscount = useCallback((item: FormItem) => {
    const mode = discountModes[item.id] || 'percent';
    const subtotal = item.quantidade * item.valor_unitario;
    if (mode === 'percent') {
      return subtotal * (item.desconto_percent / 100);
    }
    return item.desconto_fixed;
  }, [discountModes]);

  // Cálculos totais
  const subtotalGeral = useMemo(
    () => itens.reduce((sum, item) => sum + item.quantidade * item.valor_unitario, 0),
    [itens]
  );

  const totalDescontos = useMemo(
    () => itens.reduce((sum, item) => sum + calcItemDiscount(item), 0),
    [itens, calcItemDiscount]
  );

  // v2.8.0: o "Ajuste de parcelas" é uma linha automática criada pelo backend
  // em cobranças de conta SEM integração, quando o usuário altera o valor de
  // parcelas individualmente. Ela entra no total, mas nunca é editável nem
  // reenviada como produto (o backend a recria sozinho).
  const valorTotal = useMemo(
    () => subtotalGeral - totalDescontos + ajusteParcelas,
    [subtotalGeral, totalDescontos, ajusteParcelas]
  );

  const valorParcela = useMemo(
    () => tipoCobranca === 'parcelada' && numeroParcelas > 1 ? valorTotal / numeroParcelas : valorTotal,
    [valorTotal, tipoCobranca, numeroParcelas]
  );

  // -------------------------------------------------------------------------
  // Trava de valor mínimo por parcela conforme provider gateway da conta.
  // Calculada em tempo real para feedback visual antes do submit e revalidada
  // no handleSubmit. Backend (RPCs) repete a validação como camada final.
  // -------------------------------------------------------------------------
  const installmentMinimumCheck = useMemo(() => {
    const parcelasEfetivas = tipoCobranca === 'parcelada' ? numeroParcelas : 1;
    return validateInstallmentMinimum({
      providerSlug: contaSelecionada?.provider_slug ?? null,
      tipoPagamento: meioPagamentoSelecionado?.tipo_pagamento ?? null,
      valorTotal,
      numeroParcelas: parcelasEfetivas,
    });
  }, [
    contaSelecionada?.provider_slug,
    meioPagamentoSelecionado?.tipo_pagamento,
    valorTotal,
    numeroParcelas,
    tipoCobranca,
  ]);

  // === Handlers ===


  /** Remover item */
  const handleRemoveItem = useCallback((itemId: string) => {
    if (lockValueForSyncedInstallment) return;
    setItens(prev => prev.filter(i => i.id !== itemId));
  }, [lockValueForSyncedInstallment]);

  /** Atualizar campo de um item */
  const handleUpdateItem = useCallback((itemId: string, field: keyof FormItem, value: number | string) => {
    if (lockValueForSyncedInstallment) return;
    setItens(prev =>
      prev.map(i => i.id === itemId ? { ...i, [field]: value } : i)
    );
  }, [lockValueForSyncedInstallment]);

  /** Formatar moeda BRL */
  const formatBRL = (value: number) =>
    new Intl.NumberFormat('pt-BR', { style: 'currency', currency: 'BRL' }).format(value);

  // === Adaptadores para ProductCatalogModal ===

  /** Itens no formato esperado pelo ProductCatalogModal */
  const chargeItemsForModal = useMemo(
    () => itens.map(i => ({ product_id: i.product_id, quantity: i.quantidade })),
    [itens]
  );

  /** Handler: adicionar item vindo do catálogo */
  const handleCatalogAddItem = useCallback(async (productId: string, quantity: number, unitPrice: number) => {
    if (lockValueForSyncedInstallment) return;
    const existing = itens.find(i => i.product_id === productId);
    if (existing) {
      setItens(prev =>
        prev.map(i => i.product_id === productId ? { ...i, quantidade: i.quantidade + quantity } : i)
      );
      return;
    }
    const product = products.find(p => p.id === productId);
    const novoItem: FormItem = {
      id: crypto.randomUUID(),
      product_id: productId,
      product_name: product?.name || 'Produto',
      product_sku: product?.sku || null,
      descricao: product?.name || 'Produto',
      quantidade: quantity,
      valor_unitario: unitPrice,
      desconto_percent: 0,
      desconto_fixed: 0,
    };
    setItens(prev => [...prev, novoItem]);
  }, [itens, products, lockValueForSyncedInstallment]);

  /** Handler: atualizar quantidade vindo do catálogo */
  const handleCatalogUpdateQuantity = useCallback(async (productId: string, quantity: number) => {
    if (lockValueForSyncedInstallment) return;
    setItens(prev =>
      prev.map(i => i.product_id === productId ? { ...i, quantidade: quantity } : i)
    );
  }, [lockValueForSyncedInstallment]);

  /** Handler: remover item vindo do catálogo */
  const handleCatalogRemoveItem = useCallback(async (productId: string) => {
    if (lockValueForSyncedInstallment) return;
    setItens(prev => prev.filter(i => i.product_id !== productId));
  }, [lockValueForSyncedInstallment]);

  /** Reset do formulário */
  const resetForm = useCallback(() => {
    // Restaura valores prefill se existirem, senão limpa
    setCustomerId(prefillCustomerId ?? null);
    setSelectedCustomerObj(null);
    setDealId(prefillDealId ?? null);
    setCustomerSearch('');
    setTipoCobranca('unica');
    setNumeroParcelas(2);
    setDataVencimento(format(addDays(new Date(), 3), 'yyyy-MM-dd'));
    setMeioPagamentoId(null);
    setContaCorrenteId(null);
    setObservacoes('');
    setDescricao('');
    setItens([]);
    setAjusteParcelas(0);
    setDiscountModes({});
    setEditPopulated(false);
    // v2.2.0 — aplica padrões do tenant no reset (modo criação).
    // Em modo edição, o efeito de hidratação (linha ~671) sobrescreve.
    setJuros(chargeDefaults.jurosEnabled ? chargeDefaults.juros : JUROS_DEFAULT);
    setDiasExpiracao(chargeDefaults.expiracaoEnabled ? chargeDefaults.diasExpiracao : 60);
    // v2.7.0 — régua de cobrança volta ao padrão (ativa) a cada nova cobrança
    setRemindersEnabled(true);
  }, [prefillDealId, prefillCustomerId, chargeDefaults]);

  /**
   * v2.2.0 — Aplica padrões do tenant (juros/multa e dias de expiração)
   * na PRIMEIRA abertura em modo criação. Roda também quando os defaults
   * terminam de carregar após o modal já estar aberto.
   * Em modo edição, NÃO aplica — a hidratação preserva os dados reais.
   */
  const [defaultsApplied, setDefaultsApplied] = useState(false);
  useEffect(() => {
    if (!open) { setDefaultsApplied(false); return; }
    if (isEditMode) return;
    if (chargeDefaults.isLoading) return;
    if (defaultsApplied) return;
    if (chargeDefaults.jurosEnabled) setJuros(chargeDefaults.juros);
    if (chargeDefaults.expiracaoEnabled) setDiasExpiracao(chargeDefaults.diasExpiracao);
    setDefaultsApplied(true);
  }, [open, isEditMode, chargeDefaults, defaultsApplied]);




  /**
   * Efeito para pré-preencher campos quando modal abre com prefill
   * Garante que ao abrir via botão "Nova Cobrança" do deal, os campos já venham setados
   */
  useEffect(() => {
    if (open && prefillDealId) {
      setDealId(prefillDealId);
    }
    if (open && prefillCustomerId) {
      setCustomerId(prefillCustomerId);
      // Popular selectedCustomerObj quando dados do prefill chegarem
      if (prefillCustomerData) {
        setSelectedCustomerObj(prefillCustomerData as unknown as PaginatedCustomer);
      }
    }
  }, [open, prefillDealId, prefillCustomerId, prefillCustomerData]);

  /**
   * Efeito para popular formulário com dados da cobrança em modo edição
   * Executa apenas uma vez quando os detalhes são carregados com sucesso
   */
  useEffect(() => {
    if (!isEditMode || !editDetails || editPopulated) return;

    const { cobranca: cob, customer, itens: rpcItens } = editDetails;

    // Popular cliente
    setCustomerId(cob.customer_id);
    if (customer) {
      setSelectedCustomerObj({
        id: cob.customer_id,
        nome: customer.customer_type === 'pf' ? customer.name : null,
        razao_social: customer.customer_type === 'pj' ? customer.name : null,
        nome_fantasia: customer.customer_type === 'pj' ? customer.name : null,
        customer_type: customer.customer_type as 'pf' | 'pj',
        cpf: customer.cpf || null,
        cnpj: customer.cnpj || null,
        // Buscar main_contact_id real da lista de clientes já carregada
        main_contact_id: customers?.find(c => c.id === cob.customer_id)?.main_contact_id || null,
      } as unknown as PaginatedCustomer);
    }

    // Popular deal
    setDealId(cob.deal_id || null);

    // Popular tipo e parcelas
    setTipoCobranca(cob.tipo_cobranca as 'unica' | 'parcelada');
    setNumeroParcelas(cob.numero_parcelas || 2);

    // Popular vencimento
    if (cob.data_vencimento) setDataVencimento(cob.data_vencimento);

    // Popular meio de pagamento e conta corrente
    if (cob.meio_pagamento_id) setMeioPagamentoId(cob.meio_pagamento_id);
    if (cob.conta_corrente_id) setContaCorrenteId(cob.conta_corrente_id);

    // Popular observações
    setObservacoes(cob.observacoes || '');
    // Prioriza o TEMPLATE (com variáveis) quando existir; senão o texto renderizado
    setDescricao(
      (cob as { descricao_template?: string | null }).descricao_template ||
        (cob as { descricao?: string | null }).descricao ||
        '',
    );


    // v2.1.0 — hidratar juros/multa/expiração (cobrança ou herdado de assinatura)
    const cobAny = cob as unknown as Record<string, unknown>;
    const assinAny = (editDetails as unknown as { assinatura?: Record<string, unknown> }).assinatura;
    const jurosSrc = (assinAny?.juros as Record<string, unknown> | undefined) ?? (cobAny.juros as Record<string, unknown> | undefined);
    if (jurosSrc && typeof jurosSrc === 'object') {
      setJuros({
        enabled: jurosSrc.enabled === true,
        multa_tipo: (jurosSrc.multa_tipo as 'percent' | 'fixed') || 'percent',
        multa_valor: Number(jurosSrc.multa_valor) || 0,
        juros_tipo: (jurosSrc.juros_tipo as 'percent' | 'fixed') || 'percent',
        juros_valor: Number(jurosSrc.juros_valor) || 0,
      });
    }
    const diasSrc = Number(assinAny?.dias_expiracao_pos_vencimento ?? cobAny.dias_expiracao_pos_vencimento);
    if (Number.isFinite(diasSrc) && diasSrc >= 1 && diasSrc <= 60) {
      setDiasExpiracao(diasSrc);
    }

    // v2.7.0 — hidrata o estado da régua de cobrança (default true no banco)
    setRemindersEnabled((cobAny.reminders_enabled as boolean | undefined) ?? true);


    // Popular itens da cobrança
    // v2.8.1: a linha automática "Ajuste de parcelas" é identificada pelo
    // marcador estável `is_ajuste_parcelas` retornado por get_cobranca_details
    // (metadata do item OU produto virtual do tenant). O fallback por descrição
    // cobre apenas registros antigos sem produto vinculado.
    const ajusteRow = (rpcItens || []).find(
      item =>
        item.is_ajuste_parcelas === true ||
        (!item.product_id && item.descricao === AJUSTE_PARCELAS_DESCRICAO),
    );
    setAjusteParcelas(Number(ajusteRow?.total ?? 0));
    const produtosRpc = (rpcItens || []).filter(item => item !== ajusteRow);

    if (produtosRpc.length > 0) {
      const mappedItens: FormItem[] = produtosRpc.map(item => ({
        id: crypto.randomUUID(),
        product_id: item.product_id || '',
        product_name: item.product_name || item.descricao || 'Produto',
        product_sku: null,
        descricao: item.descricao || '',
        quantidade: item.quantidade,
        valor_unitario: item.valor_unitario,
        desconto_percent: 0,
        desconto_fixed: item.desconto || 0,
      }));

      // Detectar modos de desconto
      const newModes: Record<string, DiscountMode> = {};
      mappedItens.forEach(item => {
        newModes[item.id] = 'fixed'; // Itens da RPC retornam desconto absoluto
      });

      setItens(mappedItens);
      setDiscountModes(newModes);
    }

    setEditPopulated(true);
    logger.info('Formulário populado com dados da cobrança para edição', { cobrancaId: cob.id });
  }, [isEditMode, editDetails, editPopulated, customers]);

  /** Verificação se cliente possui documento (CPF/CNPJ) */
  const customerHasDocument = selectedCustomerObj
    ? !!(selectedCustomerObj.cpf || selectedCustomerObj.cnpj)
    : true; // Se não selecionou ainda, não bloquear

  /** Validação do formulário */
  const isValid = useMemo(() => {
    if (!customerId) return false;
    // Bloquear se cliente não possui CPF/CNPJ cadastrado
    if (customerId && selectedCustomerObj && !selectedCustomerObj.cpf && !selectedCustomerObj.cnpj) return false;
    if (itens.length === 0) return false;
    if (valorTotal < VALOR_MINIMO_COBRANCA) return false;
    if (!meioPagamentoId) return false;
    if (!contaCorrenteId) return false;
    if (!dataVencimento) return false;
    return true;
  }, [customerId, selectedCustomerObj, itens, valorTotal, meioPagamentoId, contaCorrenteId, dataVencimento]);

  /** Submeter cobrança (criação ou edição) */
  const handleSubmit = async () => {
    if (!isValid || !customerId || !meioPagamentoId || !contaCorrenteId) return;

    if (
      lockValueForSyncedInstallment &&
      editDetails?.cobranca?.valor_total !== undefined &&
      Number(editDetails.cobranca.valor_total) !== Number(valorTotal.toFixed(2))
    ) {
      toast.error(
        'Cobranças parceladas não permitem edição de valor (itens, quantidade, preço, desconto). Para alterar o valor, cancele e crie uma nova cobrança. Vencimento, descrição, juros/multa e expiração continuam editáveis.',
        { duration: 9000 }
      );
      return;
    }

    // -----------------------------------------------------------------------
    // Trava de valor mínimo por parcela do provider gateway. Evita ida ao
    // provedor (ex.: Asaas exige R$ 5,00 por parcela em Boleto). Backend
    // (RPCs create/update_financeiro_cobranca) revalida como camada final.
    // -----------------------------------------------------------------------
    if (!installmentMinimumCheck.valid && installmentMinimumCheck.message) {
      toast.error(installmentMinimumCheck.message, { duration: 9000 });
      return;
    }



    // Converter desconto para valor absoluto esperado pelo hook
    const chargeItens: ChargeItemInput[] = itens.map(i => ({
      product_id: i.product_id,
      descricao: i.descricao,
      quantidade: i.quantidade,
      valor_unitario: i.valor_unitario,
      desconto: Number(calcItemDiscount(i).toFixed(2)),
    }));

    // === MODO EDIÇÃO ===
    // Cobrança SYNCED com provider → PROVIDER-FIRST (bloqueante)
    //   Frontend chama Edge Function `financial-charge` (action update_payment).
    //   A Edge Function: PUT no provider → só depois persiste local.
    //   Se provider falhar, NADA é salvo localmente e o usuário vê o erro real.
    //   Para parcelamento, itera por parcela (Asaas exige PUT por parcela).
    //
    // Cobrança LOCAL-ONLY → chama RPC direto (comportamento antigo).
    if (isEditMode && editCobrancaId) {
      setIsSaving(true);
      try {
        const descricaoFinal = descricao.trim() || itens.map(i => i.descricao).join(', ');
        const isSynced = !!editDetails?.cobranca?.provider_charge_id;
        const isParcelada = editDetails?.cobranca?.tipo_cobranca === 'parcelada';
        const parcelas = editDetails?.parcelas || [];

        if (isSynced) {
          // === PROVIDER-FIRST ===
          if (isParcelada) {
            // -----------------------------------------------------------------
            // CORREÇÃO 2026-06-18 (v3):
            // Voltamos a usar a RPC `update_financeiro_cobranca` v3.0.0 como
            // FONTE ÚNICA atômica. Ela:
            //   - Atualiza itens + parcelas (UPDATE in place preservando
            //     metadata.provider_payment_id) + cobrança-pai em UMA transação.
            //   - Enfileira `q_charge` (1 msg por parcela synced) → worker
            //     financial-charge-worker faz PUT no Asaas async (mesmo padrão
            //     de recorrência).
            //   - Bloqueia mudança de tipo_cobranca / numero_parcelas quando
            //     synced (validação interna da própria RPC).
            //
            // Por que isso é seguro: a transação garante que itens NUNCA são
            // perdidos (rollback total em falha). Caso ACAA3112 onde DELETE
            // commitou e INSERT falhou separadamente na Edge não pode ocorrer.
            // -----------------------------------------------------------------
            const chargeItensFn: ChargeItemInput[] = itens.map(i => ({
              product_id: i.product_id,
              descricao: i.descricao,
              quantidade: i.quantidade,
              valor_unitario: i.valor_unitario,
              desconto: Number(calcItemDiscount(i).toFixed(2)),
            }));

            const { error: rpcError } = await supabase.rpc('update_financeiro_cobranca', {
              p_cobranca_id: editCobrancaId,
              p_descricao: descricaoFinal || null,
              p_observacoes: observacoes || null,
              p_data_vencimento: dataVencimento,
              p_meio_pagamento_id: meioPagamentoId,
              p_conta_corrente_id: contaCorrenteId,
              p_tipo_cobranca: 'parcelada',
              p_numero_parcelas: numeroParcelas,
              p_itens: chargeItensFn as unknown as never,
              p_juros: (juros.enabled ? juros : null) as unknown as never,
              p_dias_expiracao: diasExpiracao,
              // v2.7.0 — régua de cobrança salva junto com os demais campos
              p_reminders_enabled: remindersEnabled,
            });

            if (rpcError) {
              throw new Error(rpcError.message || 'Falha ao atualizar cobrança parcelada');
            }

            toast.success('Cobrança atualizada localmente. Status ficará "Pendente" até a confirmação do provedor.');
          } else {
            // Cobrança avulsa synced — payload completo via modal premium
            const chargeItensFn: ChargeItemInput[] = itens.map(i => ({
              product_id: i.product_id,
              descricao: i.descricao,
              quantidade: i.quantidade,
              valor_unitario: i.valor_unitario,
              desconto: Number(calcItemDiscount(i).toFixed(2)),
            }));
            const { data: result, error: fnError } = await supabase.functions.invoke(
              'financial-charge',
              {
                body: {
                  action: 'update_payment',
                  cobranca_id: editCobrancaId,
                  conta_corrente_id: contaCorrenteId,
                  valor_total: valorTotal,
                  data_vencimento: dataVencimento,
                  descricao: descricaoFinal || null,
                  observacoes: observacoes || null,
                  tipo_pagamento: meios?.find(m => m.id === meioPagamentoId)?.tipo_pagamento,
                  meio_pagamento_id: meioPagamentoId,
                  tipo_cobranca: tipoCobranca,
                  itens: chargeItensFn,
                  juros: juros.enabled ? juros : {},
                  dias_expiracao: diasExpiracao,
                  // v2.7.0 — régua de cobrança (repassado à RPC pela Edge)
                  reminders_enabled: remindersEnabled,
                },
              }
            );
            if (fnError) throw new Error(fnError.message);
            if (result && result.success === false) {
              throw new Error(result.error || 'Erro ao atualizar cobrança no provedor');
            }
            toast.success('Cobrança atualizada com sucesso no provedor.');
          }

        } else {
          // === LOCAL-ONLY: caminho histórico (RPC direto) ===
          const chargeItens: ChargeItemInput[] = itens.map(i => ({
            product_id: i.product_id,
            descricao: i.descricao,
            quantidade: i.quantidade,
            valor_unitario: i.valor_unitario,
            desconto: Number(calcItemDiscount(i).toFixed(2)),
          }));
          const { error: rpcError } = await supabase.rpc('update_financeiro_cobranca', {
            p_cobranca_id: editCobrancaId,
            p_descricao: descricaoFinal || null,
            p_observacoes: observacoes || null,
            p_data_vencimento: dataVencimento,
            p_meio_pagamento_id: meioPagamentoId,
            p_conta_corrente_id: contaCorrenteId,
            p_tipo_cobranca: tipoCobranca,
            p_numero_parcelas: tipoCobranca === 'parcelada' ? numeroParcelas : 1,
            p_itens: chargeItens as unknown as never,
            p_juros: (juros.enabled ? juros : {}) as unknown as never,
            p_dias_expiracao: diasExpiracao as unknown as never,
            // v2.7.0 — régua de cobrança salva junto com os demais campos
            p_reminders_enabled: remindersEnabled as unknown as never,
          });
          if (rpcError) throw rpcError;
          toast.success('Cobrança atualizada com sucesso!');
        }

        // Persiste o template quando a descrição contém variáveis, re-renderizando
        // o snapshot gravado em `descricao` (fonte do checkout/provedor).
        if (editCobrancaId && hasDescricaoVariables(descricaoFinal)) {
          await persistDescricaoTemplate({ template: descricaoFinal, cobrancaId: editCobrancaId });
        }


        queryClient.invalidateQueries({ queryKey: ['financeiro-cobrancas'] });
        queryClient.invalidateQueries({ queryKey: ['financeiro-cobrancas-paginated'] });
        queryClient.invalidateQueries({ queryKey: ['financeiro-parcelas'] });
        queryClient.invalidateQueries({ queryKey: ['cobranca-details'] });
        resetForm();
        onOpenChange(false);
      } catch (err) {
        logger.error('Erro ao atualizar cobrança', { error: err });
        const e = err as { message?: string; details?: string; hint?: string };
        const msg = e?.message || e?.details || e?.hint || 'Erro ao salvar alterações';
        toast.error(msg, { duration: 8000 });
      } finally {
        setIsSaving(false);
      }
      return;
    }



    // === MODO CRIAÇÃO: fluxo normal ===
    await createCharge.mutateAsync({
      customer_id: customerId,
      deal_id: dealId || undefined,
      // Vínculo com a tarefa de origem (quando aberto pela aba Financeiro da tarefa)
      task_id: prefillTaskId || undefined,
      tipo_cobranca: tipoCobranca,
      meio_pagamento_id: meioPagamentoId,
      conta_corrente_id: contaCorrenteId,
      itens: chargeItens,
      data_vencimento: dataVencimento,
      numero_parcelas: tipoCobranca === 'parcelada' ? numeroParcelas : 1,
      observacoes: observacoes || undefined,
      descricao: descricao.trim() || undefined,
      // v2.1.0 — juros/multa/expiração (avulsa e parcelada)
      juros: juros.enabled ? (juros as unknown as Record<string, unknown>) : {},
      dias_expiracao: diasExpiracao,
      // v2.7.0 — régua de cobrança escolhida na criação
      reminders_enabled: remindersEnabled,
      // Veículos vinculados (gravados na mesma transação da cobrança)
      vehicle_ids: vehicleIds.length > 0 ? vehicleIds : undefined,
      // Conta sem integração => não despachar ao provedor (controle manual)
      skip_provider_dispatch: isContaManual,
    });

    resetForm();
    onOpenChange(false);
  };


  return (
    <Dialog open={open} onOpenChange={(v) => { if (!v) resetForm(); onOpenChange(v); }}>
      <DialogContent className="max-w-5xl max-h-[90vh] flex flex-col p-0 gap-0">
        {/* Header */}
        <DialogHeader className="p-6 pb-4 border-b shrink-0">
          <div className="flex items-center gap-3">
            <div className="p-2.5 rounded-xl bg-primary/10">
              <Receipt className="h-5 w-5 text-primary" />
            </div>
            <div>
              <DialogTitle className="text-lg">{isEditMode ? 'Editar Cobrança' : 'Nova Cobrança'}</DialogTitle>
              <DialogDescription className="text-sm">
                {isEditMode
                  ? 'Altere os dados da cobrança existente'
                  : 'Preencha os dados para gerar uma nova cobrança financeira'}
              </DialogDescription>
            </div>
          </div>
        </DialogHeader>

        {/* Corpo com scroll */}
        <ScrollArea className="flex-1 overflow-auto">
          <div className="p-6 space-y-6">

            {/* Aviso: edição da cobrança-pai parcelada propaga para parcelas não-finais */}
            {isEditMode && editDetails?.cobranca?.tipo_cobranca === 'parcelada' && (
              <InlineNotice variant="warning">
                <p className="text-xs">
                  As alterações realizadas nesta cobrança-pai (vencimento, descrição, juros/multa e prazo de expiração) serão propagadas automaticamente para todas as parcelas-filhas que ainda não estejam em status final (pago, cancelado ou estornado). Parcelas finalizadas permanecem intactas.
                </p>
              </InlineNotice>
            )}

            {/* === SEÇÃO 1: Cliente === */}
            <section>
              <div className="flex items-center gap-2 mb-3">
                <User className="h-4 w-4 text-primary" />
                <h3 className="text-sm font-semibold text-foreground">Cliente</h3>
                <span className="text-xs text-destructive">*</span>
              </div>
              {/* Seletor de cliente + botão de criar novo lado a lado */}
              <div className="flex items-center gap-2">
              <Popover open={customerPopoverOpen} onOpenChange={setCustomerPopoverOpen}>
                <PopoverTrigger asChild>
                  <Button
                    variant="outline"
                    role="combobox"
                    aria-expanded={customerPopoverOpen}
                    disabled={!!lockDeal || isEditMode}
                    className="w-full justify-between"
                  >
                    {selectedCustomerObj ? (
                      <div className="flex items-center gap-2 truncate">
                        <Badge variant="outline" className="text-[10px] px-1.5 shrink-0">
                          {selectedCustomerObj.customer_type === 'pj' ? 'PJ' : 'PF'}
                        </Badge>
                        <span className="truncate">{getCustomerDisplayNamePaginated(selectedCustomerObj)}</span>
                        {getCustomerDocument(selectedCustomerObj) && (
                          <span className="text-xs text-muted-foreground ml-1 shrink-0">
                            {formatDocumentForDisplay(getCustomerDocument(selectedCustomerObj))}
                          </span>
                        )}
                      </div>
                    ) : (
                      <span className="text-muted-foreground">Selecione um cliente...</span>
                    )}
                    <ChevronsUpDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
                  </Button>
                </PopoverTrigger>
                <PopoverContent className="w-[--radix-popover-trigger-width] p-0" align="start">
                  <Command shouldFilter={false}>
                    <CommandInput
                      placeholder="Buscar por nome, CPF/CNPJ, telefone, email..."
                      value={customerSearch}
                      onValueChange={setCustomerSearch}
                    />
                    <CommandList>
                      <CommandEmpty>
                        {loadingCustomers ? 'Buscando...' : 'Nenhum cliente encontrado.'}
                      </CommandEmpty>
                      <CommandGroup>
                        {customers.map((customer) => (
                          <CommandItem
                            key={customer.id}
                            value={customer.id}
                            onSelect={() => {
                              setCustomerId(customer.id);
                              setSelectedCustomerObj(customer);
                              setDealId(null); // Resetar negócio ao trocar cliente
                              setCustomerPopoverOpen(false);
                              setCustomerSearch('');
                            }}
                          >
                            <div className="flex items-center gap-2 flex-1 min-w-0">
                              <Badge variant="outline" className="text-[10px] px-1.5 shrink-0">
                                {customer.customer_type === 'pj' ? 'PJ' : 'PF'}
                              </Badge>
                              <div className="flex flex-col flex-1 min-w-0">
                                <span className="truncate font-medium">
                                  {getCustomerDisplayNamePaginated(customer)}
                                </span>
                                {(getCustomerDocument(customer) || customer.main_contact_phone || customer.main_contact_email) && (
                                  <span className="text-xs text-muted-foreground truncate">
                                    {[formatDocumentForDisplay(getCustomerDocument(customer)), customer.main_contact_phone, customer.main_contact_email]
                                      .filter(Boolean)
                                      .join(' • ')}
                                  </span>
                                )}
                              </div>
                            </div>
                            <Check
                              className={cn(
                                'ml-2 h-4 w-4 shrink-0',
                                customerId === customer.id ? 'opacity-100' : 'opacity-0'
                              )}
                            />
                          </CommandItem>
                        ))}
                      </CommandGroup>
                    </CommandList>
                  </Command>
                </PopoverContent>
              </Popover>
                {/* Botão para criar novo cliente sem sair do modal */}
                {/* Botão para criar novo cliente sem sair do modal */}
                <Button
                  type="button"
                  variant="outline"
                  size="icon"
                  title="Criar novo cliente"
                  className="shrink-0"
                  disabled={!!lockDeal || isEditMode}
                  onClick={() => setCreateCustomerOpen(true)}
                >
                  <Plus className="h-4 w-4" />
                </Button>

                {/* Seletor de negócio vinculado ao cliente (Popover+Command com busca) */}
                {/* Seletor de negócio: desabilitado quando lockDeal=true (cobrança via modal do deal) */}
                <Popover open={dealPopoverOpen} onOpenChange={setDealPopoverOpen}>
                  <PopoverTrigger asChild>
                    <Button
                      variant="outline"
                      role="combobox"
                      aria-expanded={dealPopoverOpen}
                      disabled={!customerId || !!lockDeal || isEditMode}
                      className="w-[300px] shrink-0 justify-between font-normal"
                    >
                      {dealId && customerDeals?.find(d => d.id === dealId) ? (
                        <span className="flex items-center gap-2 truncate">
                          <span className="text-xs font-mono text-muted-foreground">
                            {formatDealCode(dealId)}
                          </span>
                          <span className="truncate">
                            {customerDeals.find(d => d.id === dealId)?.title}
                          </span>
                        </span>
                      ) : dealId && (isEditMode || lockDeal) ? (
                        /* Fallback: deal vinculado mas lista ainda não carregou */
                        <span className="flex items-center gap-2 truncate text-muted-foreground">
                          <span className="text-xs font-mono">
                            {formatDealCode(dealId)}
                          </span>
                          <span className="truncate">Carregando...</span>
                        </span>
                      ) : (
                        <span className="text-muted-foreground">Vincular negócio (opcional)</span>
                      )}
                      <ChevronsUpDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
                    </Button>
                  </PopoverTrigger>
                  <PopoverContent className="w-[340px] p-0" align="start">
                    <Command>
                      <CommandInput placeholder="Buscar negócio..." />
                      <CommandList>
                        <CommandEmpty>Nenhum negócio encontrado</CommandEmpty>
                        <CommandGroup>
                          {/* Opção para desvincular */}
                          <CommandItem
                            value="__none__"
                            onSelect={() => {
                              setDealId(null);
                              setDealPopoverOpen(false);
                            }}
                          >
                            <span className="text-muted-foreground">Nenhum negócio</span>
                            {!dealId && <Check className="ml-auto h-4 w-4" />}
                          </CommandItem>
                          {customerDeals?.map((deal) => (
                            <CommandItem
                              key={deal.id}
                              value={`${formatDealCode(deal.id)} ${deal.title}`}
                              onSelect={() => {
                                setDealId(deal.id);
                                setDealPopoverOpen(false);
                              }}
                            >
                              <div className="flex flex-col gap-1 w-full min-w-0">
                                <div className="flex items-center gap-2">
                                  <span className="text-[11px] font-mono text-muted-foreground shrink-0">
                                    {formatDealCode(deal.id)}
                                  </span>
                                  <span className="text-sm font-medium truncate">{deal.title}</span>
                                </div>
                                <div className="flex items-center justify-between text-xs">
                                  <span className="text-muted-foreground">
                                    {formatCurrency(deal.value || 0, { currency: deal.currency || 'BRL' })}
                                  </span>
                                  <DealStatusBadgePremium status={deal.status} size="xs" />
                                </div>
                              </div>
                              {dealId === deal.id && <Check className="ml-2 h-4 w-4 shrink-0" />}
                            </CommandItem>
                          ))}
                        </CommandGroup>
                      </CommandList>
                    </Command>
                  </PopoverContent>
                </Popover>

                {/* Botão para criar novo negócio sem sair do modal */}
                <Button
                  type="button"
                  variant="outline"
                  size="icon"
                  title="Criar novo negócio"
                  className="shrink-0"
                  disabled={!customerId || !!lockDeal || isEditMode}
                  onClick={() => setCreateDealOpen(true)}
                >
                  <Plus className="h-4 w-4" />
                </Button>
              </div>

              {/* Alerta CPF/CNPJ ausente — bloqueia criação da cobrança */}
              {customerId && selectedCustomerObj && !selectedCustomerObj.cpf && !selectedCustomerObj.cnpj && (
                <Alert variant="destructive" className="mt-2">
                  <AlertTriangle className="h-4 w-4" />
                  <AlertDescription>
                    Cliente sem CPF/CNPJ cadastrado. Complete o cadastro antes de gerar a cobrança.
                  </AlertDescription>
                </Alert>
              )}
            </section>

            {/* Modal de criação de cliente inline */}
            <CustomerFormDialog
              customer={null}
              open={createCustomerOpen}
              onOpenChange={setCreateCustomerOpen}
              onSuccess={(newCustomer) => {
                // Pré-seleciona o cliente recém-criado no campo de seleção
                if (newCustomer) {
                  setCustomerId(newCustomer.id);
                  setSelectedCustomerObj({
                    id: newCustomer.id,
                    nome: newCustomer.customer_type === 'pf' ? newCustomer.nome : null,
                    razao_social: newCustomer.customer_type === 'pj' ? newCustomer.nome : null,
                    nome_fantasia: newCustomer.customer_type === 'pj' ? newCustomer.nome : null,
                    customer_type: newCustomer.customer_type as 'pf' | 'pj',
                    cpf: newCustomer.cpf || null,
                    cnpj: newCustomer.cnpj || null,
                    contact_phone: newCustomer.contact_phone || null,
                    contact_email: newCustomer.contact_email || null,
                    main_contact_id: newCustomer.main_contact_id,
                  } as unknown as PaginatedCustomer);
                  setDealId(null);
                }
                setCustomerSearch('');
                setDebouncedCustomerSearch('');
              }}
              onSelectExistingCustomer={(dup) => {
                // Pré-seleciona o cliente duplicado no campo de seleção da cobrança
                setCustomerId(dup.id);
                setSelectedCustomerObj({
                  id: dup.id,
                  nome: dup.customer_type === 'pf' ? dup.nome : null,
                  razao_social: dup.customer_type === 'pj' ? dup.nome : null,
                  nome_fantasia: dup.customer_type === 'pj' ? dup.nome : null,
                  customer_type: dup.customer_type as 'pf' | 'pj',
                  cpf: dup.cpf,
                  cnpj: dup.cnpj,
                  contact_phone: dup.contact_phone,
                  contact_email: dup.contact_email,
                  main_contact_id: dup.main_contact_id,
                } as unknown as PaginatedCustomer);
                setDealId(null); // Resetar negócio ao trocar cliente
                setCreateCustomerOpen(false);
                toast.success(`Cliente "${dup.nome}" selecionado automaticamente.`);
              }}
            />

            {/* Modal de criação de negócio inline */}
            <DealFormDialog
              open={createDealOpen}
              onOpenChange={setCreateDealOpen}
              /* Seleção inline: o negócio é escolhido aqui mesmo, sem modal de sucesso */
              showSuccessDialog={false}
              defaultContactId={selectedCustomerObj?.main_contact_id || undefined}
              onSuccess={(newDeal) => {
                // Auto-seleciona o negócio recém-criado no seletor
                if (newDeal) {
                  setDealId(newDeal.id);
                  // Invalida query para que o novo deal apareça na lista
                  queryClient.invalidateQueries({ queryKey: ['customer-deals-for-charge'] });
                }
              }}
            />

            <Separator />

            {/* === SEÇÃO 1.5: Descrição (livre, editável) — posicionada logo após o Cliente === */}
            <section>
              <div className="flex items-center gap-2 mb-3">
                <FileText className="h-4 w-4 text-muted-foreground" />
                <h3 className="text-sm font-semibold text-foreground">Descrição</h3>
                <span className="text-[10px] text-muted-foreground">(opcional)</span>
              </div>
              {/* v3.0.0 — campo com suporte a variáveis dinâmicas ({{...}}) */}
              <DescricaoTemplateField
                value={descricao}
                onChange={setDescricao}
                placeholder="Breve descrição da cobrança..."
                maxLength={255}
                customerId={customerId || null}
                vehicleIds={vehicleIds}
                cobrancaId={editCobrancaId || null}
              />
              <p className="text-[11px] text-muted-foreground mt-1">
                Se vazia, será gerada automaticamente a partir dos itens. Use variáveis para manter o texto sempre atualizado.
              </p>

            </section>

            <Separator />

            {/* === SEÇÃO 2: Itens da Cobrança === */}
            <section>
              <div className="flex items-center justify-between mb-3">
                <div className="flex items-center gap-2">
                  <Package className="h-4 w-4 text-primary" />
                  <h3 className="text-sm font-semibold text-foreground">Itens da Cobrança</h3>
                  <span className="text-xs text-destructive">*</span>
                  {itens.length > 0 && (
                    <Badge variant="secondary" className="text-xs">
                      {itens.length} {itens.length === 1 ? 'item' : 'itens'}
                    </Badge>
                  )}
                </div>
                {itens.length > 0 && (
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => setCatalogModalOpen(true)}
                    disabled={lockValueForSyncedInstallment}
                    className="h-7 text-xs"
                    title={lockValueForSyncedInstallment ? 'Cobranças parceladas não permitem edição de valor. Para alterar, cancele e crie uma nova cobrança.' : undefined}
                  >
                    <Plus className="h-3.5 w-3.5 mr-1" />
                    Adicionar
                  </Button>
                )}
              </div>

              {lockValueForSyncedInstallment && (
                <div className="mb-3">
                  <InlineNotice variant="warning">
                    <p className="text-xs">
                      Cobranças parceladas não permitem edição de valor (itens, quantidade, preço, desconto). Para alterar o valor, cancele e crie uma nova cobrança. Vencimento, descrição, juros/multa e expiração continuam editáveis.
                    </p>
                  </InlineNotice>
                </div>
              )}

              {/* Lista de itens */}
              {itens.length === 0 ? (
                <div className="border border-dashed border-border rounded-lg p-8 text-center">
                  <ShoppingCart className="h-10 w-10 text-muted-foreground mx-auto mb-3" />
                  <p className="text-sm font-medium text-muted-foreground mb-1">
                    Nenhum produto adicionado
                  </p>
                  <p className="text-xs text-muted-foreground mb-4">
                    Adicione produtos ou serviços do catálogo para compor esta cobrança
                  </p>
                  <Button variant="outline" onClick={() => setCatalogModalOpen(true)} disabled={lockValueForSyncedInstallment}>
                    <Plus className="h-4 w-4 mr-2" />
                    Adicionar Produto ou Serviço
                  </Button>
                </div>
              ) : (
                <div className="space-y-3">
                  {/* Tabela de itens — layout igual ao DealItemsSummary */}
                  <div className="border border-border/50 rounded-lg overflow-hidden">
                    <ScrollArea className="max-h-[250px]">
                      <Table>
                        <TableHeader>
                          <TableRow className="bg-muted/30">
                            <TableHead className="text-xs h-8">Produto</TableHead>
                            <TableHead className="text-xs h-8 w-20 text-center">Qtd</TableHead>
                            <TableHead className="text-xs h-8 w-24 text-right">Preço</TableHead>
                            <TableHead className="text-xs h-8 w-28 text-center">Desconto</TableHead>
                            <TableHead className="text-xs h-8 w-24 text-right">Total</TableHead>
                            <TableHead className="text-xs h-8 w-10" />
                          </TableRow>
                        </TableHeader>
                        <TableBody>
                          {itens.map((item) => {
                            const itemSubtotal = item.quantidade * item.valor_unitario;
                            const itemDiscount = calcItemDiscount(item);
                            const itemTotal = itemSubtotal - itemDiscount;
                            const currentMode = discountModes[item.id] || 'percent';
                            // Ícone baseado no tipo (produto padrão, pode expandir se houver product_type)
                            const TypeIcon = Package;

                            return (
                              <TableRow key={item.id} className="group">
                                {/* Produto */}
                                <TableCell className="py-2">
                                  <div className="flex items-center gap-2">
                                    <TypeIcon className="h-3.5 w-3.5 shrink-0 text-amber-500" />
                                    <div className="min-w-0">
                                      <p className="text-xs font-medium truncate">{item.product_name}</p>
                                      {item.product_sku && (
                                        <p className="text-[10px] text-muted-foreground">{item.product_sku}</p>
                                      )}
                                    </div>
                                  </div>
                                </TableCell>

                                {/* Quantidade */}
                                <TableCell className="py-2">
                                  <InlineEditableField
                                    value={item.quantidade}
                                      onChange={(qty) => {
                                        if (!lockValueForSyncedInstallment) handleUpdateItem(item.id, 'quantidade', Math.max(1, qty));
                                      }}
                                    min={1}
                                    allowDecimals={false}
                                      className={cn('h-7 w-16 text-xs text-center mx-auto', lockValueForSyncedInstallment && 'opacity-60 pointer-events-none')}
                                  />
                                </TableCell>

                                {/* Preço Unitário — editável quando o produto é de preço aberto */}
                                <TableCell className="py-2 text-right">
                                  {products.find(p => p.id === item.product_id)?.is_open_price &&
                                  !lockValueForSyncedInstallment ? (
                                    /* Máscara monetária BR (R$) para itens de preço sob demanda */
                                    <CurrencyInput
                                      value={item.valor_unitario}
                                      onChange={(val) =>
                                        handleUpdateItem(item.id, 'valor_unitario', Math.max(0, val))
                                      }
                                      className="h-7 w-28 text-xs text-right ml-auto"
                                    />
                                  ) : (
                                    <span className="text-xs">
                                      {formatCurrency(item.valor_unitario)}
                                    </span>
                                  )}
                                </TableCell>


                                {/* Desconto (% ou R$) */}
                                <TableCell className="py-2">
                                  <div className="flex items-center gap-1 justify-center">
                                    <DiscountModeToggle
                                      mode={currentMode}
                                      onToggle={(newMode) => {
                                        if (lockValueForSyncedInstallment) return;
                                        setDiscountModes(prev => ({ ...prev, [item.id]: newMode }));
                                        handleUpdateItem(item.id, 'desconto_percent', 0);
                                        handleUpdateItem(item.id, 'desconto_fixed', 0);
                                      }}
                                    />
                                    <InlineEditableField
                                      key={`${item.id}-${currentMode}`}
                                      value={currentMode === 'percent' ? item.desconto_percent : item.desconto_fixed}
                                      onChange={(val) => {
                                        if (lockValueForSyncedInstallment) return;
                                        const field = currentMode === 'percent' ? 'desconto_percent' : 'desconto_fixed';
                                        const maxVal = currentMode === 'percent' ? 100 : itemSubtotal;
                                        handleUpdateItem(item.id, field, Math.min(val, maxVal));
                                      }}
                                      min={0}
                                      max={currentMode === 'percent' ? 100 : itemSubtotal}
                                      placeholder="-"
                                      className={cn('h-7 w-16 text-xs text-center', lockValueForSyncedInstallment && 'opacity-60 pointer-events-none')}
                                    />
                                  </div>
                                </TableCell>

                                {/* Total */}
                                <TableCell className="py-2 text-right">
                                  <span className="text-xs font-medium">
                                    {formatCurrency(itemTotal)}
                                  </span>
                                </TableCell>

                                {/* Ação remover */}
                                <TableCell className="py-2">
                                  <Button
                                    variant="ghost"
                                    size="icon"
                                    className="h-7 w-7 opacity-0 group-hover:opacity-100 transition-opacity"
                                    disabled={lockValueForSyncedInstallment}
                                    onClick={() => handleRemoveItem(item.id)}
                                    title={lockValueForSyncedInstallment ? 'Cobranças parceladas não permitem edição de valor. Para alterar, cancele e crie uma nova cobrança.' : undefined}
                                  >
                                    <Trash2 className="h-3.5 w-3.5 text-destructive" />
                                  </Button>
                                </TableCell>
                              </TableRow>
                            );
                          })}
                        </TableBody>
                      </Table>
                    </ScrollArea>
                  </div>

                  {/* Totalizadores — 3 cards lado a lado */}
                  <div className="flex gap-3">
                    {/* Subtotal */}
                    <div className="flex-1 p-3 rounded-lg border bg-muted/30 border-border/50">
                      <div className="flex items-center gap-2 mb-1">
                        <ShoppingCart className="h-4 w-4 text-muted-foreground" />
                        <span className="text-xs text-muted-foreground">Subtotal</span>
                      </div>
                      <p className="text-lg font-semibold">{formatBRL(subtotalGeral)}</p>
                    </div>
                    {/* Descontos */}
                    <div className="flex-1 p-3 rounded-lg border bg-orange-500/10 border-orange-500/30">
                      <div className="flex items-center gap-2 mb-1">
                        <Percent className="h-4 w-4 text-orange-500" />
                        <span className="text-xs text-muted-foreground">Descontos</span>
                      </div>
                      <p className="text-lg font-semibold text-orange-600">-{formatBRL(totalDescontos)}</p>
                    </div>
                    {/* v2.8.0 — Ajuste de parcelas (automático, somente leitura) */}
                    {ajusteParcelas !== 0 && (
                      <div className="flex-1 p-3 rounded-lg border bg-muted/30 border-border/50">
                        <div className="flex items-center gap-2 mb-1">
                          <Calculator className="h-4 w-4 text-muted-foreground" />
                          <span className="text-xs text-muted-foreground">Ajuste de parcelas</span>
                        </div>
                        <p className="text-lg font-semibold">
                          {ajusteParcelas > 0 ? '+' : '-'}{formatBRL(Math.abs(ajusteParcelas))}
                        </p>
                      </div>
                    )}
                    {/* Total */}
                    <div className="flex-1 p-3 rounded-lg border bg-primary/10 border-primary/30">
                      <div className="flex items-center gap-2 mb-1">
                        <Calculator className="h-4 w-4 text-primary" />
                        <span className="text-xs text-muted-foreground">Total</span>
                      </div>
                      <p className="text-lg font-semibold text-primary">{formatBRL(valorTotal)}</p>
                    </div>
                  </div>

                  {/* Alerta valor mínimo Asaas */}
                  {valorTotal > 0 && valorTotal < VALOR_MINIMO_COBRANCA && (
                    <Alert variant="destructive" className="mt-3">
                      <AlertTriangle className="h-4 w-4" />
                      <AlertDescription>
                        O valor mínimo para cobrança no Asaas é de <strong>R$ 5,00</strong>. 
                        Ajuste a quantidade, valor ou desconto dos itens para atingir o valor mínimo.
                      </AlertDescription>
                    </Alert>
                  )}
                </div>
              )}
            </section>

            {/* Modal do catálogo de produtos */}
            <ProductCatalogModal
              open={catalogModalOpen}
              onOpenChange={setCatalogModalOpen}
              dealId=""
              currency="BRL"
              dealItems={chargeItemsForModal}
              onAddItem={handleCatalogAddItem}
              onUpdateItemQuantity={handleCatalogUpdateQuantity}
              onRemoveItem={handleCatalogRemoveItem}
            />

            {!isEditMode && <Separator />}


            {/* === SEÇÃO 3: Tipo da Cobrança === */}
            {/* Em modo edição o seletor de tipo é ocultado: o provider (ex.: Asaas) */}
            {/* já emitiu com o tipo original e não aceita alteração posterior. */}
            {/* Para trocar o tipo, é necessário cancelar e criar uma nova cobrança. */}
            <section>

              {!isEditMode && (
                <>
                  <div className="flex items-center gap-2 mb-3">
                    <FileText className="h-4 w-4 text-primary" />
                    <h3 className="text-sm font-semibold text-foreground">Tipo da Cobrança</h3>
                    {/* Quando vinculada a uma recorrência, sinaliza que o tipo é imutável */}
                    {isRecurrenceCharge && (
                      <Badge variant="outline" className="text-[10px]">
                        Vinculada à recorrência
                      </Badge>
                    )}
                  </div>
                  <div className="grid grid-cols-2 gap-2">
                    <button
                      type="button"
                      disabled={isRecurrenceCharge}
                      onClick={() => !isRecurrenceCharge && setTipoCobranca('unica')}
                      className={cn(
                        'p-3 rounded-lg border-2 text-left transition-all',
                        tipoCobranca === 'unica'
                          ? 'border-primary bg-primary/5 shadow-sm'
                          : 'border-border hover:border-primary/40',
                        isRecurrenceCharge && 'opacity-60 cursor-not-allowed hover:border-border'
                      )}
                    >
                      <div className="font-medium text-sm">Única</div>
                      <div className="text-xs text-muted-foreground">Pagamento em uma vez</div>
                    </button>
                    <button
                      type="button"
                      disabled={isRecurrenceCharge}
                      onClick={() => !isRecurrenceCharge && setTipoCobranca('parcelada')}
                      className={cn(
                        'p-3 rounded-lg border-2 text-left transition-all',
                        tipoCobranca === 'parcelada'
                          ? 'border-primary bg-primary/5 shadow-sm'
                          : 'border-border hover:border-primary/40',
                        isRecurrenceCharge && 'opacity-60 cursor-not-allowed hover:border-border'
                      )}
                    >
                      <div className="font-medium text-sm">Parcelada</div>
                      <div className="text-xs text-muted-foreground">Dividir em parcelas</div>
                    </button>
                  </div>

                  {/* Aviso quando o tipo está bloqueado por vínculo com recorrência */}
                  {isRecurrenceCharge && (
                    <p className="text-xs text-muted-foreground mt-2">
                      O tipo não pode ser alterado em cobranças geradas por uma recorrência.
                    </p>
                  )}
                </>
              )}

              



              {/* Seletor de parcelas — não exibido para cobranças de recorrência */}
              {tipoCobranca === 'parcelada' && !isRecurrenceCharge && (
                <div className="mt-3 p-3 bg-muted/40 rounded-lg border border-border">
                  <Label className="text-xs text-muted-foreground mb-2 block">Número de Parcelas</Label>
                  <div className="flex flex-wrap gap-1.5">
                    {[2, 3, 4, 5, 6, 7, 8, 9, 10, 11, 12].map(n => (
                      <button
                        key={n}
                        type="button"
                        // v3.0.0: cobrança já emitida no provedor não permite trocar nº de parcelas
                        disabled={isSyncedCharge}
                        onClick={() => !isSyncedCharge && setNumeroParcelas(n)}
                        className={cn(
                          'h-8 w-10 rounded-md text-xs font-medium transition-all border',
                          numeroParcelas === n
                            ? 'bg-primary text-primary-foreground border-primary shadow-sm'
                            : 'bg-background border-border hover:border-primary/50 text-foreground',
                          isSyncedCharge && 'opacity-60 cursor-not-allowed hover:border-border'
                        )}
                      >
                        {n}x
                      </button>
                    ))}
                  </div>
                  {valorTotal > 0 && (
                    <div className="mt-2 text-xs text-muted-foreground">
                      {numeroParcelas}x de{' '}
                      <span className="font-semibold text-foreground">{formatBRL(valorParcela)}</span>
                    </div>
                  )}
                  {/* Aviso de valor mínimo por parcela conforme provider gateway. */}
                  {!installmentMinimumCheck.valid && installmentMinimumCheck.message && (
                    <div className="mt-3">
                      <InlineNotice variant="warning">
                        <p className="text-xs">{installmentMinimumCheck.message}</p>
                      </InlineNotice>
                    </div>
                  )}
                  {isSyncedCharge && (
                    <div className="mt-3">
                      <InlineNotice variant="warning">
                        <p className="text-xs">
                          Esta cobrança já foi emitida no provedor. O número de parcelas não
                          pode ser alterado. Valor, vencimento, juros/multa, descrição e meio
                          de pagamento serão sincronizados automaticamente.
                        </p>
                      </InlineNotice>
                    </div>
                  )}
                </div>
              )}
            </section>


            <Separator />

            {/* === SEÇÃO 4: Vencimento === */}
            <section>
              <div className="flex items-center gap-2 mb-3">
                <CalendarDays className="h-4 w-4 text-primary" />
                <h3 className="text-sm font-semibold text-foreground">
                  {tipoCobranca === 'parcelada' ? 'Vencimento da 1ª Parcela' : 'Data de Vencimento'}
                </h3>
                <span className="text-xs text-destructive">*</span>
              </div>
              <Input
                type="date"
                value={dataVencimento}
                onChange={(e) => setDataVencimento(e.target.value)}
                min={format(new Date(), 'yyyy-MM-dd')}
                className="max-w-[200px]"
              />
              {tipoCobranca === 'parcelada' && (
                <p className="text-xs text-muted-foreground mt-1">
                  As demais parcelas vencem mês a mês a partir desta data
                </p>
              )}

              {/* === Regra de emissão herdada da assinatura (read-only) === */}
              {/* Exibida apenas em modo edição quando a cobrança veio de uma recorrência. */}
              {/* A regra de emissão (tipo_emissao + dias_antes_emissao / dia_emissao) é */}
              {/* propriedade da assinatura — não pode ser alterada por ciclo individual. */}
              {isRecurrenceCharge && editDetails?.assinatura && (() => {
                const a = editDetails.assinatura;
                const tipo = a.tipo_emissao || 'dias_antes';
                const dias = a.dias_antes_emissao ?? 0;
                const dia = a.dia_emissao ?? null;
                const status = editDetails.cobranca?.status;
                const venc = editDetails.cobranca?.data_vencimento;

                // Calcula a data prevista de emissão usando a MESMA regra do
                // cron `emitir_ciclos_due` no backend (v1.1.2):
                //   - dias_antes: data_vencimento - dias_antes_emissao
                //   - dia_fixo : min(dia_emissao, último dia do mês de vencimento)
                let dataPrevista: string | null = null;
                if (venc) {
                  const [yy, mm, dd] = venc.split('-').map(Number);
                  if (tipo === 'dia_fixo' && dia) {
                    const ultimoDiaMes = new Date(yy, mm, 0).getDate();
                    const diaFinal = Math.min(dia, ultimoDiaMes);
                    dataPrevista = `${String(diaFinal).padStart(2, '0')}/${String(mm).padStart(2, '0')}/${yy}`;
                  } else {
                    const base = new Date(yy, mm - 1, dd);
                    base.setDate(base.getDate() - dias);
                    dataPrevista = `${String(base.getDate()).padStart(2, '0')}/${String(base.getMonth() + 1).padStart(2, '0')}/${base.getFullYear()}`;
                  }
                }

                const regraTxt = tipo === 'dia_fixo' && dia
                  ? `Emissão automática no dia ${dia} de cada mês, conforme a assinatura.`
                  : `Emissão automática ${dias} dia${dias === 1 ? '' : 's'} antes do vencimento, conforme a assinatura.`;

                return (
                  <div className="mt-3">
                    <InlineNotice variant="info" icon={CalendarClock}>
                      <div className="space-y-1">
                        <p className="text-sm font-medium">Regra de emissão (herdada da recorrência)</p>
                        <p className="text-xs">{regraTxt}</p>
                        {status === 'aguardando_emissao' && dataPrevista && (
                          <p className="text-xs">
                            Esta cobrança será emitida em <strong>{dataPrevista}</strong>.
                          </p>
                        )}
                        <p className="text-[11px] text-muted-foreground">
                          Para alterar, edite a assinatura.
                        </p>
                      </div>
                    </InlineNotice>
                  </div>
                );
              })()}
            </section>


            <Separator />

            {/* === SEÇÃO 5: Conta Corrente (antes dos meios — filtra a lista) === */}
            <section>
              <div className="flex items-center gap-2 mb-3">
                <Building2 className="h-4 w-4 text-primary" />
                <h3 className="text-sm font-semibold text-foreground">Conta Corrente</h3>
                <span className="text-xs text-destructive">*</span>
              </div>
              {loadingContas ? (
                <div className="flex items-center gap-2 text-sm text-muted-foreground">
                  <Loader2 className="h-4 w-4 animate-spin" /> Carregando...
                </div>
              ) : contasDisponiveis.length === 0 ? (
                <div className="flex items-center gap-2 p-3 bg-destructive/10 rounded-lg text-sm text-destructive">
                  <AlertTriangle className="h-4 w-4" />
                  Nenhuma conta corrente ativa cadastrada.
                </div>
              ) : (
                <Select
                  value={contaCorrenteId || ''}
                  onValueChange={(v) => setContaCorrenteId(v || null)}
                  disabled={isEditMode}
                >
                  <SelectTrigger className="w-full">
                    <SelectValue placeholder="Selecione a conta corrente..." />
                  </SelectTrigger>
                  <SelectContent>
                    {contasDisponiveis.map(conta => (
                      <SelectItem key={conta.id} value={conta.id}>
                        <div className="flex items-center gap-2">
                          <span>{conta.nome}</span>
                          {/* Sem provider => controle manual */}
                          <Badge variant="outline" className="text-[10px]">
                            {conta.provider_slug || 'Manual'}
                          </Badge>
                        </div>
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              )}
              {/* Aviso de conta sem integração (cobrança apenas de controle interno) */}
              {isContaManual && (
                <div className="mt-2">
                  <InlineNotice variant="info">
                    Conta sem integração de pagamento — a cobrança será apenas de
                    controle interno (sem boleto/PIX automático) e a baixa deve ser
                    registrada manualmente.
                  </InlineNotice>
                </div>
              )}
              {isEditMode && (
                <p className="text-[11px] text-muted-foreground mt-1.5">
                  A conta corrente não pode ser alterada após a criação da cobrança.
                </p>
              )}

            </section>

            <Separator />

            {/* === SEÇÃO 6: Meio de Pagamento (filtrado pela conta selecionada) === */}
            <section>
              <div className="flex items-center gap-2 mb-3">
                <CreditCard className="h-4 w-4 text-primary" />
                <h3 className="text-sm font-semibold text-foreground">Meio de Pagamento</h3>
                <span className="text-xs text-destructive">*</span>
              </div>
              {loadingMeios ? (
                <div className="flex items-center gap-2 text-sm text-muted-foreground">
                  <Loader2 className="h-4 w-4 animate-spin" /> Carregando...
                </div>
              ) : !contaCorrenteId ? (
                /* Conta ainda não selecionada: orienta o usuário antes de listar meios */
                <InlineNotice variant="info">
                  Selecione a conta corrente para ver os meios de pagamento disponíveis.
                </InlineNotice>
              ) : meiosDaConta.length === 0 ? (
                <div className="flex items-center gap-2 p-3 bg-destructive/10 rounded-lg text-sm text-destructive">
                  <AlertTriangle className="h-4 w-4" />
                  Nenhum meio de pagamento ativo para esta conta. Configure em Configurações &gt; Meios de Pagamento.
                </div>
              ) : (
                <div className="grid grid-cols-2 sm:grid-cols-3 gap-2">
                  {meiosDaConta.map(meio => (
                    <button
                      key={meio.id}
                      type="button"
                      disabled={lockPaymentMethodForSyncedInstallment}
                      onClick={() => {
                        if (!lockPaymentMethodForSyncedInstallment) setMeioPagamentoId(meio.id);
                      }}
                      title={
                        lockPaymentMethodForSyncedInstallment
                          ? 'Não é possível trocar a forma de pagamento de um parcelamento já emitido no provedor'
                          : undefined
                      }
                      className={cn(
                        'p-3 rounded-lg border-2 text-left transition-all',
                        meioPagamentoId === meio.id
                          ? 'border-primary bg-primary/5 shadow-sm'
                          : 'border-border hover:border-primary/40',
                        lockPaymentMethodForSyncedInstallment && 'opacity-60 cursor-not-allowed hover:border-border'
                      )}
                    >
{(() => {
                        const IconComp = PaymentMethodIcon[meio.tipo_pagamento] || DefaultPaymentIcon;
                        return <div className="mb-1"><IconComp className="h-6 w-6 text-muted-foreground" /></div>;
                      })()}
                      <div className="font-medium text-xs truncate">{meio.nome}</div>
                    </button>
                  ))}
                </div>
              )}
              {lockPaymentMethodForSyncedInstallment && (
                <p className="mt-2 text-xs text-muted-foreground">
                  Não é possível trocar a forma de pagamento de um parcelamento já emitido no provedor.
                </p>
              )}
            </section>




            <Separator />


            {/* === SEÇÃO 6.5: Juros/Multa + Expiração (paridade c/ assinatura) === */}
            {/* v2.1.0 — Reaproveita 100% os mesmos componentes de assinatura,
                garantindo identidade visual e comportamento entre os 3 tipos de cobrança
                (avulsa, parcelada e recorrência). */}
            <section className="space-y-3">
              <SubscriptionJurosSection value={juros} onChange={setJuros} defaultOpen={false} />
              {/* v2.3.0 — Conta sem integração (controle manual) não possui
                  boleto/link emitido no provedor, logo não há prazo de expiração. */}
              {!isContaManual && (
                <SubscriptionExpiracaoSection value={diasExpiracao} onChange={setDiasExpiracao} defaultOpen={false} />
              )}

              {/* v2.7.0 — Lembretes automáticos (régua de cobrança).
                  A escolha é salva JUNTO com os demais campos da cobrança;
                  o modal de detalhes apenas exibe o estado (somente leitura). */}
              <div className="flex items-center justify-between gap-3 rounded-lg border border-border px-3 py-2.5">
                <div className="flex items-start gap-2 min-w-0">
                  <Bell className="h-4 w-4 text-primary mt-0.5 shrink-0" />
                  <div className="min-w-0">
                    <p className="text-sm font-medium text-foreground">Lembretes automáticos</p>
                    <p className="text-xs text-muted-foreground">
                      Quando desligado, esta cobrança não recebe as notificações da régua de cobrança.
                    </p>
                  </div>
                </div>
                <Switch
                  checked={remindersEnabled}
                  onCheckedChange={setRemindersEnabled}
                  aria-label="Ativar ou suspender lembretes da régua de cobrança"
                />
              </div>
            </section>

            {/* === SEÇÃO 7: Observações === */}
            <section>
              <div className="flex items-center gap-2 mb-3">
                <FileText className="h-4 w-4 text-muted-foreground" />
                <h3 className="text-sm font-semibold text-foreground">Observações</h3>
                <span className="text-[10px] text-muted-foreground">(opcional)</span>
              </div>
              <Textarea
                placeholder="Notas internas sobre esta cobrança..."
                value={observacoes}
                onChange={(e) => setObservacoes(e.target.value)}
                rows={3}
                className="resize-none"
                maxLength={isProviderAsaas ? MAX_OBS_ASAAS : undefined}
              />
              {/* Contador de caracteres — visível apenas quando conta Asaas selecionada */}
              {isProviderAsaas && (
                <p className="text-xs text-muted-foreground text-right mt-1">
                  {observacoes.length}/{MAX_OBS_ASAAS} caracteres
                </p>
              )}
            </section>

            {/* === SEÇÃO 8: Veículos vinculados (módulo Veículos) === */}
            <VehiclePickerSection
              customerId={customerId}
              contactId={selectedCustomerObj?.main_contact_id ?? null}
              value={vehicleIds}
              onChange={setVehicleIds}
              enabled={open}
              className="pt-1"
            />
          </div>
        </ScrollArea>

        {/* Footer */}
        <div className="p-4 border-t shrink-0 flex items-center justify-between">
          <div className="text-sm text-muted-foreground">
            {valorTotal > 0 && valorTotal < VALOR_MINIMO_COBRANCA ? (
              <span className="text-destructive font-medium">
                ⚠ Valor abaixo do mínimo (R$ 5,00)
              </span>
            ) : valorTotal > 0 ? (
              <span>
                Total: <span className="font-bold text-foreground">{formatBRL(valorTotal)}</span>
                {tipoCobranca === 'parcelada' && ` (${numeroParcelas}x ${formatBRL(valorParcela)})`}
              </span>
            ) : null}
          </div>
          <div className="flex items-center gap-2">
            <Button
              variant="outline"
              onClick={() => { resetForm(); onOpenChange(false); }}
              disabled={isCreating || isSaving}
            >
              Cancelar
            </Button>
            <Button
              onClick={handleSubmit}
              disabled={!isValid || isCreating || isSaving || (isEditMode && loadingEditDetails)}
              className="min-w-[140px]"
            >
              {(isCreating || isSaving) ? (
                <>
                  <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                  {isEditMode ? 'Salvando...' : 'Gerando...'}
                </>
              ) : (
                <>
                  <Receipt className="h-4 w-4 mr-2" />
                  {isEditMode ? 'Salvar Alterações' : 'Gerar Cobrança'}
                </>
              )}
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}
